import { useSyncExternalStore } from 'react'

/**
 * Desktop-viewport detection, at the SAME 768px boundary the app's own
 * `useIsPhone` uses to swap in the mobile shell — so a component and the shell
 * around it can never disagree about which presentation is in force.
 *
 * Media-query based, not user-agent: DevTools emulation, real phones and split
 * windows all behave identically, and a resize re-picks live.
 *
 * Lives in `shared/core-ui` rather than in either feature that uses it.
 * `features/files` and `features/org-connections` BOTH need it — the second is
 * the Outsiders tab of the first's dialog — and `features/files` already
 * imports `OutsiderTab`, so a home in `features/files` would close a cycle.
 * It cannot come from `apps/web` (where `useIsPhone` lives) either: features
 * may depend on `shared/*`, never on the app shell.
 *
 * Server snapshot is `true` — the desktop layout is the one that needs no JS to
 * be correct, so it is the safe thing to render before hydration.
 */
const DESKTOP_QUERY = '(min-width: 768px)'

export function useIsDesktop(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(DESKTOP_QUERY)
      m.addEventListener('change', cb)
      return () => m.removeEventListener('change', cb)
    },
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => true,
  )
}
